import "./vendor/cdt-resolve.bundle.js";
import { verticalFragments, type ContiguityPlanItem } from "./blockContiguity";
import { slacksOf } from "../domain/bayShare";
import { PLACEHOLDER_VALUE } from "../domain/studioTypes";
import { avoidableWithinFamilies, type SizeOrderPlan } from "./sizeOrdering";

export { PLACEHOLDER_VALUE } from "../domain/studioTypes";

const RESOLVER_SIZE_ATTR = "__sizeLabel";

export interface ResolverCanvasItem {
  id: string;
  shelf: number;
  x: number;
  w: number;
  hf: number;
  h: number;
  node: string;
  ix: number;
  sec?: string;
  brand?: string;
  desc?: string;
}

export interface ResolverCanvas {
  runs: readonly { run: number; items: readonly ResolverCanvasItem[] }[];
  geo: readonly { shelves: readonly { rank: number; pitch: number }[] }[];
  bayW: number;
  pins: readonly ResolverPin[];
}

export interface ResolverItemsDoc {
  ITEMS: readonly { id: string; a: Readonly<Record<string, string | null>> }[];
  SPINE: readonly string[];
  ORIENT: Readonly<Record<string, "horizontal" | "vertical">>;
  ORDER: Readonly<Record<string, readonly string[]>>;
  LABELS?: Readonly<Record<string, string>>;
}

export interface ResolverPin {
  kind: string;
  scope: string;
  node?: string;
  ids: readonly string[];
  seqOrder?: readonly string[];
  /** Studio rejoin only — never autoResolve. */
  fragment?: boolean;
}

export interface ResolverVerdict {
  resolved: boolean;
  detail: string;
  [extra: string]: unknown;
}

export interface ResolverAuditSummary {
  inverted: number;
  invertedAvoidable: number;
  overlaps: number;
  overruns: number;
  tooTall: number;
  laneBreaks: number;
  splitNodes: number;
  clean: boolean;
}

export interface ResolverDiff {
  fixed?: number;
  introduced: number;
  gradeFixed?: number;
  gradeWorse?: number;
  newBreaks?: readonly string[];
  newSplits?: readonly string[];
  [extra: string]: unknown;
}

export interface ResolverStep {
  pin: string;
  kind: string;
  action: "relocate" | "resequence" | "grade" | "none";
  ok: boolean;
  resolved: boolean;
  partial?: boolean;
  alreadyClean?: boolean;
  physicalChange?: boolean;
  mode?: string;
  reason?: string;
  notes: readonly string[];
  verdict?: ResolverVerdict;
  diff: ResolverDiff;
  newPos?: Readonly<Record<string, ResolverPosition>>;
  audit?: ResolverAuditSummary;
  graded?: readonly {
    node: string;
    was: { count: number; forced: number; avoidable: number };
    now: { count: number; forced: number; avoidable: number };
    restacked: number;
    pinned: readonly unknown[];
  }[];
  skipped?: readonly { node: string; why: string }[];
  pinned?: readonly { node: string; id: string; h: number; shelf: number }[];
}

export interface ResolverPosition {
  run: number;
  shelf: number;
  x: number;
  hf: number;
}

export interface ResolverStudio {
  pins: readonly ResolverPin[];
  positions: () => Readonly<Record<string, ResolverPosition>>;
  audit: () => ResolverAuditSummary;
  check: (pin: ResolverPin) => ResolverVerdict;
  resolve: (pin: ResolverPin) => ResolverStep;
  preview: (pin: ResolverPin) => ResolverStep;
  resolveAll: () => {
    steps: readonly ResolverStep[];
    newPos: Readonly<Record<string, ResolverPosition>>;
    audit: ResolverAuditSummary;
  };
  reset: () => void;
}

interface CdtResolveGlobal {
  attach: (
    canvas: ResolverCanvas,
    itemsDoc: ResolverItemsDoc,
    opts?: { runW?: number; sizeRule?: "ascending" | "listed" },
  ) => ResolverStudio;
}

/** Canvas shelf -> the row rank a merchant reads, for studios whose canvas skips empty hang lines. */
const rankOfShelfByStudio = new WeakMap<ResolverStudio, (shelf: number) => number>();

const shelfNamesOf = (studio: ResolverStudio, shelves: readonly number[]): string => {
  const rankOf = rankOfShelfByStudio.get(studio);
  return (rankOf ? shelves.map(rankOf) : shelves).join("–");
};

export const attachResolver = (
  canvas: ResolverCanvas,
  itemsDoc: ResolverItemsDoc,
  {
    rankOfShelf,
    ...opts
  }: { runW?: number; sizeRule?: "listed"; rankOfShelf?: (shelf: number) => number } = {},
): ResolverStudio => {
  const cdtResolve = (globalThis as { CDTResolve?: CdtResolveGlobal }).CDTResolve;
  if (!cdtResolve) throw new Error("cdt-resolve bundle did not attach to globalThis");
  const studio = cdtResolve.attach(canvas, itemsDoc, opts);
  if (rankOfShelf) rankOfShelfByStudio.set(studio, rankOfShelf);
  return studio;
};

interface PlanInternals {
  items: Map<string, Record<string, unknown>>;
  /** The plan's own bay, raw units. */
  bayW?: number;
  clone: () => PlanInternals;
}
interface CdtResolveOps {
  ops: {
    resequence: (
      plan: unknown,
      ids: string[],
      target: string[],
      opts: unknown,
    ) => { ok: boolean; notes?: string[] };
    relocate: (
      plan: unknown,
      seedIds: string[],
      opts: unknown,
    ) => { ok: boolean; reason?: string; notes?: string[] };
    stretchesOf: (
      plan: unknown,
      ids: readonly string[],
    ) => { run: number; x0: number; x1: number; span: number; ids: string[] }[];
  };
  targetOrder: (plan: unknown, pin: ResolverPin) => string[];
  Plan: {
    right: (item: ContiguityPlanItem) => number;
    width: (item: ContiguityPlanItem) => number;
  };
}

interface PlanForGroups {
  get: (
    id: string,
  ) => { x: number; w: number; hf: number; path: readonly (string | null)[] } | undefined;
}

export const groupsInOrder = (studio: ResolverStudio, pin: ResolverPin): boolean => {
  const cdtResolve = (
    globalThis as unknown as {
      CDTResolve: CdtResolveOps & {
        audit: {
          sequenceBreaks: (
            plan: unknown,
            ids: readonly string[],
            orientation: string,
          ) => { order: readonly string[] };
        };
      };
    }
  ).CDTResolve;
  const plan = (
    studio as unknown as {
      plan: PlanForGroups & {
        cdtCompare: (a: unknown, b: unknown) => number;
        spine: readonly string[];
      };
    }
  ).plan;
  const depth = pin.scope.split(" › ").filter((segment) => segment !== "*").length;
  const { order } = cdtResolve.audit.sequenceBreaks(plan, [...pin.ids], "horizontal");
  const read = order.map((id) => plan.get(id)).filter(Boolean) as {
    path: readonly (string | null)[];
    run?: number;
    shelf?: number;
  }[];
  for (let i = 1; i < read.length; i++) {
    const a = read[i - 1];
    const b = read[i];
    // Cross-shelf backwards is the shelf split, not a reorder defect.
    if (a.run !== b.run || a.shelf !== b.shelf) continue;
    // Placeholder is missing data, not an inversion.
    if (PLACEHOLDER_VALUE.test(String(a.path[depth] ?? ""))) continue;
    if (PLACEHOLDER_VALUE.test(String(b.path[depth] ?? ""))) continue;
    let firstDiff = -1;
    for (let level = 0; level < plan.spine.length; level++) {
      if (String(a.path[level]) !== String(b.path[level])) {
        firstDiff = level;
        break;
      }
    }
    if (firstDiff === depth && plan.cdtCompare(a, b) > 0) return false;
  }
  const lanesByValue = new Map<string, Set<number>>();
  for (const entry of read) {
    const value = String(entry.path[depth] ?? "");
    if (PLACEHOLDER_VALUE.test(value)) continue;
    const laneSet = lanesByValue.get(value) ?? new Set<number>();
    laneSet.add((entry.run ?? 0) * 100 + (entry.shelf ?? 0));
    lanesByValue.set(value, laneSet);
  }
  for (const laneSet of lanesByValue.values()) {
    if (laneSet.size < 2) continue;
    const shelves = [...laneSet].sort((a, b) => a - b);
    for (const lane of shelves) {
      if (shelves.every((other) => other === lane || Math.abs(other - lane) > 1)) return false;
    }
  }
  return true;
};

export const gradientResidue = (studio: ResolverStudio, pin: ResolverPin): number => {
  const cdtResolve = (
    globalThis as unknown as {
      CDTResolve: {
        grade: { inversions: (plan: unknown, ids: readonly string[]) => { avoidable: number } };
      };
    }
  ).CDTResolve;
  const plan = (studio as unknown as { plan: unknown }).plan;
  return cdtResolve.grade.inversions(plan, [...pin.ids]).avoidable;
};

/** Shared with detection: below this, residue is reported not flagged. */
export const GRADIENT_PIN_THRESHOLD = 5;

export const sizeOrderSatisfied = (studio: ResolverStudio, pin: ResolverPin): boolean => {
  const cdtResolve = (
    globalThis as unknown as {
      CDTResolve: {
        grade: { inversions: (plan: unknown, ids: readonly string[]) => { avoidable: number } };
      };
    }
  ).CDTResolve;
  const plan = (studio as unknown as { plan: unknown }).plan as SizeOrderPlan;
  if (!groupsInOrder(studio, pin)) return false;
  const avoidable = avoidableWithinFamilies(
    plan,
    [...pin.ids],
    plan.spine.indexOf(RESOLVER_SIZE_ATTR),
    (model, ids) => cdtResolve.grade.inversions(model, ids),
  );
  return avoidable < GRADIENT_PIN_THRESHOLD;
};

/** Per-shelf can pass while groups still read against the tree. Restores on audit worsen. */
export const resequenceFallback = (studio: ResolverStudio, pin: ResolverPin): ResolverStep => {
  const cdtResolve = (globalThis as unknown as { CDTResolve: CdtResolveOps }).CDTResolve;
  const plan = (studio as unknown as { plan: PlanInternals }).plan;
  const snapshot = plan.clone();
  const before = studio.audit();
  // Resequence stays inside the closest block — fragments must not increase.
  const fragmentsBefore = pinBlockFragments(studio, pin).length;
  const restore = () => {
    plan.items.clear();
    snapshot.items.forEach((value, key) => plan.items.set(key, { ...value }));
  };
  const noop = (reason: string): ResolverStep => ({
    pin: pin.scope,
    kind: pin.kind,
    action: "none",
    ok: true,
    resolved: false,
    alreadyClean: true,
    physicalChange: false,
    reason,
    notes: [],
    diff: { introduced: 0 },
  });
  let result: { ok: boolean; notes?: string[] };
  try {
    result = cdtResolve.ops.resequence(plan, [...pin.ids], cdtResolve.targetOrder(plan, pin), {
      orientation: "horizontal",
    });
  } catch {
    restore();
    return noop("Not applied: the order could not be changed");
  }
  const after = studio.audit();
  const worsened =
    after.overlaps > before.overlaps ||
    after.overruns > before.overruns ||
    after.tooTall > before.tooTall ||
    after.laneBreaks > before.laneBreaks ||
    after.splitNodes > before.splitNodes ||
    after.invertedAvoidable > before.invertedAvoidable ||
    pinBlockFragments(studio, pin).length > fragmentsBefore;
  if (!result.ok || worsened) {
    restore();
    return noop(
      result.ok
        ? "Not applied: reordering would break another block"
        : "Not applied: no room on the shelf to reorder",
    );
  }
  // Must actually be in tree order, and something must have moved.
  const fixed = groupsInOrder(studio, pin);
  let movedCount = 0;
  plan.items.forEach((item, key) => {
    const was = snapshot.items.get(key) as { x?: number; shelf?: number; hf?: number } | undefined;
    const now = item as { x?: number; shelf?: number; hf?: number };
    if (!was || was.shelf !== now.shelf || was.hf !== now.hf) movedCount++;
    else if (Math.abs((was.x ?? 0) - (now.x ?? 0)) > slacksOf(plan.bayW).nudge) movedCount++;
  });
  if (!fixed && movedCount === 0) {
    return {
      pin: pin.scope,
      kind: pin.kind,
      action: "resequence",
      ok: true,
      resolved: false,
      partial: true,
      physicalChange: false,
      reason: "Each shelf is already in order; the offset comes from how the block spans shelves",
      notes: [],
      diff: { introduced: 0 },
    };
  }
  return {
    pin: pin.scope,
    kind: pin.kind,
    action: "resequence",
    ok: true,
    resolved: fixed,
    partial: !fixed,
    physicalChange: movedCount > 0,
    reason: fixed ? undefined : `${movedCount} facing(s) moved toward the flow; no room to finish`,
    notes: [`reordered toward the flow (${movedCount} facing(s) moved)`, ...(result.notes ?? [])],
    verdict: {
      resolved: fixed,
      detail: fixed
        ? "groups read in tree order along the shelf"
        : "groups still offset by the block's shelf split",
    },
    diff: { introduced: 0 },
  };
};

interface PlanForFragments {
  all: () => ContiguityPlanItem[];
  /** The plan's own bay, raw units. */
  bayW?: number;
  nodeKey: (item: ContiguityPlanItem, depth: number) => string;
  spine: readonly string[];
  items: Map<string, Record<string, unknown>>;
  clone: () => PlanForFragments;
}

const planOf = (studio: ResolverStudio): PlanForFragments =>
  (studio as unknown as { plan: PlanForFragments }).plan;

const fragmentGradeDepth = (plan: PlanForFragments): number =>
  Math.max(1, plan.spine.indexOf("__sizeLabel"));

export const pinBlockFragments = (studio: ResolverStudio, pin: ResolverPin) => {
  const plan = planOf(studio);
  const depth = fragmentGradeDepth(plan);
  const node = pin.scope.split(" › ").slice(0, depth).join(" › ");
  const blockFragments = verticalFragments(plan, depth, cdtResolveGlobal().Plan, node);
  const nodeDepth = Math.min(pin.scope.split(" › ").length, depth);
  const ownNode = pin.scope.split(" › ").slice(0, nodeDepth).join(" › ");
  const own = plan
    .all()
    .filter((item) => plan.nodeKey(item, nodeDepth) === ownNode)
    .map((item) => item.id);
  const scatter: ReturnType<typeof verticalFragments> = [];
  const claimed = new Set(blockFragments.flatMap((frag) => frag.ids));
  // Fragment pins already name strays — re-listing would relocate twice.
  const wholeNodePin = pin.scope === ownNode;
  if (wholeNodePin && own.length > 1) {
    try {
      const stretches = cdtResolveGlobal()
        .ops.stretchesOf(planOf(studio), own)
        .sort((a, b) => b.span - a.span || b.ids.length - a.ids.length);
      for (const stretch of stretches.slice(1)) {
        if (stretch.ids.every((id) => claimed.has(id))) continue;
        scatter.push({
          node: ownNode,
          run: stretch.run,
          shelves: [],
          ids: stretch.ids,
          mainRun: stretches[0].run,
          mainShelves: [],
        });
      }
    } catch {
      /* stretch measure unavailable */
    }
  }
  const sizeFragments = verticalFragments(plan, depth + 1, cdtResolveGlobal().Plan).filter(
    (fragment) => fragment.node === node || fragment.node.startsWith(`${node} › `),
  );
  const sizeClaimed = new Set(sizeFragments.flatMap((frag) => frag.ids));
  const scatterClean = scatter.filter((frag) => !frag.ids.every((id) => sizeClaimed.has(id)));
  return [...blockFragments, ...sizeFragments, ...scatterClean];
};

const cdtResolveGlobal = (): CdtResolveOps =>
  (globalThis as unknown as { CDTResolve: CdtResolveOps }).CDTResolve;

/** Contiguity first so a stray's foreign shelf is not in the grade footprint. */
export const rejoinFragments = (studio: ResolverStudio, pin: ResolverPin): ResolverStep | null => {
  const fragments = pinBlockFragments(studio, pin);
  if (!fragments.length) return null;
  const cdtResolve = cdtResolveGlobal();
  const plan = planOf(studio);
  const snapshot = plan.clone();
  const restore = () => {
    plan.items.clear();
    snapshot.items.forEach((value, key) => plan.items.set(key, { ...value }));
  };
  const before = studio.audit();
  const orderBefore = blockOrderSignature(studio);
  const notes: string[] = [];
  let moved = 0;
  const nodeGather = fragments.some((fragment) => fragment.shelves.length === 0);
  if (nodeGather) {
    const gatherNode = fragments.find((fragment) => fragment.shelves.length === 0)!.node;
    let result: { ok: boolean; reason?: string; notes?: string[] };
    try {
      // Empty seeds are rejected before opts.node is read.
      result = cdtResolve.ops.relocate(plan, [...pin.ids], { node: gatherNode });
    } catch {
      result = { ok: false, reason: "relocate failed" };
    }
    if (result.ok) {
      moved += 1;
      notes.push(`gathered ${gatherNode} into one block`);
    } else {
      notes.push(`${gatherNode} could not be moved together: ${result.reason ?? "no room"}`);
    }
  }
  for (const fragment of fragments) {
    if (fragment.shelves.length === 0) continue;
    let result: { ok: boolean; reason?: string; notes?: string[] };
    try {
      result = cdtResolve.ops.relocate(plan, [...fragment.ids], {});
    } catch {
      result = { ok: false, reason: "relocate failed" };
    }
    if (!result.ok) {
      notes.push(
        `${fragment.ids.length} facing(s) on shelf ${shelfNamesOf(studio, fragment.shelves)} could not rejoin the block: ${result.reason ?? "no room"}`,
      );
      continue;
    }
    moved += fragment.ids.length;
    notes.push(
      `rejoined ${fragment.ids.length} facing(s) from shelf ${shelfNamesOf(studio, fragment.shelves)} into the block (shelves ${shelfNamesOf(studio, fragment.mainShelves)})`,
    );
  }
  const after = studio.audit();
  const remaining = pinBlockFragments(studio, pin);
  const regressions: string[] = [];
  if (after.overlaps > before.overlaps) regressions.push("overlaps");
  if (after.overruns > before.overruns) regressions.push("overhang");
  if (after.tooTall > before.tooTall) regressions.push("packs too tall for the shelf");
  if (after.laneBreaks > before.laneBreaks) regressions.push("broken blocks");
  if (after.splitNodes > before.splitNodes) regressions.push("split blocks");
  // Rejoin must not change relative block order.
  if (blockOrderViolations(orderBefore, blockOrderSignature(studio)).length) {
    regressions.push("a flow change");
  }
  if (remaining.length > fragments.length) regressions.push("more block pieces");
  if (regressions.length) {
    restore();
    return {
      pin: pin.scope,
      kind: pin.kind,
      action: "relocate",
      ok: true,
      resolved: false,
      physicalChange: false,
      reason: `Not applied: rejoining the block would cause ${regressions.join(", ")}`,
      notes,
      diff: { introduced: 0 },
    };
  }
  if (moved === 0) {
    return {
      pin: pin.scope,
      kind: pin.kind,
      action: "relocate",
      ok: true,
      resolved: false,
      physicalChange: false,
      reason: notes.join("; "),
      notes,
      diff: { introduced: 0 },
    };
  }
  return {
    pin: pin.scope,
    kind: pin.kind,
    action: "relocate",
    ok: true,
    resolved: remaining.length === 0,
    partial: remaining.length > 0,
    physicalChange: true,
    reason: remaining.length
      ? `${remaining.length} piece(s) could not rejoin the block`
      : undefined,
    notes,
    diff: { introduced: 0 },
  };
};

/** Shelf wrap is fine; foreign kin between members is not. */
export const readingConsecutive = (studio: ResolverStudio, node: string): boolean => {
  const cdtResolve = (
    globalThis as unknown as {
      CDTResolve: CdtResolveOps & {
        audit: {
          sequenceBreaks: (
            plan: unknown,
            ids: readonly string[],
            orientation: string,
          ) => { order: readonly string[] };
        };
      };
    }
  ).CDTResolve;
  const plan = planOf(studio);
  const depth = node.split(" › ").length;
  const parent = node.split(" › ").slice(0, -1).join(" › ");
  const parentIds = plan
    .all()
    .filter((item) => plan.nodeKey(item, depth - 1) === parent)
    .map((item) => item.id);
  const reading = cdtResolve.audit.sequenceBreaks(planOf(studio), parentIds, "horizontal").order;
  const positions: number[] = [];
  reading.forEach((id, index) => {
    const item = plan.all().find((entry) => entry.id === id);
    if (item && plan.nodeKey(item, depth) === node) positions.push(index);
  });
  if (positions.length < 2) return true;
  return Math.max(...positions) - Math.min(...positions) === positions.length - 1;
};

/** Shared x-window: a vertical box is split in reading order by design. */
export const xAligned = (studio: ResolverStudio, node: string): boolean => {
  const plan = planOf(studio);
  const helpers = cdtResolveGlobal().Plan;
  const depth = node.split(" › ").length;
  const byShelf = new Map<string, { min: number; max: number }>();
  for (const item of plan.all()) {
    if (plan.nodeKey(item, depth) !== node) continue;
    const key = `${item.run}|${item.shelf}`;
    const entry = byShelf.get(key) ?? { min: Infinity, max: -Infinity };
    entry.min = Math.min(entry.min, item.x);
    entry.max = Math.max(entry.max, helpers.right(item));
    byShelf.set(key, entry);
  }
  const intervals = [...byShelf.values()];
  if (intervals.length < 2) return true;
  const common = {
    min: Math.max(...intervals.map((interval) => interval.min)),
    max: Math.min(...intervals.map((interval) => interval.max)),
  };
  const widths = intervals.map((interval) => interval.max - interval.min).sort((a, b) => a - b);
  const median = widths[Math.floor(widths.length / 2)] || slacksOf(plan.bayW).edge;
  return common.max - common.min >= 0.4 * median;
};

export const fragmentsSatisfied = (studio: ResolverStudio, pin: ResolverPin): boolean => {
  const plan = planOf(studio);
  const depth = fragmentGradeDepth(plan);
  const nodeDepth = Math.min(pin.scope.split(" › ").length, depth);
  const ownNode = pin.scope.split(" › ").slice(0, nodeDepth).join(" › ");
  if (pin.scope === ownNode && nodeDepth < depth) {
    return readingConsecutive(studio, ownNode) || xAligned(studio, ownNode);
  }
  return pinBlockFragments(studio, pin).length === 0;
};

export const blockOrderSignature = (studio: ResolverStudio): Map<string, number> => {
  const plan = planOf(studio);
  const helpers = cdtResolveGlobal().Plan;
  const depthMax = fragmentGradeDepth(plan);
  const relations = new Map<string, number>();
  for (let depth = 1; depth < depthMax; depth++) {
    interface ValueStat {
      value: string;
      width: number;
      sumX: number;
      sumShelf: number;
      minX: number;
      maxX: number;
      runs: Set<number>;
      shelves: Set<number>;
    }
    const byParent = new Map<string, Map<string, ValueStat>>();
    for (const item of plan.all()) {
      const parent = item.path.slice(0, depth).map(String).join("/");
      const value = String(item.path[depth]);
      const values = byParent.get(parent) ?? new Map<string, ValueStat>();
      const stat =
        values.get(value) ??
        ({
          value,
          width: 0,
          sumX: 0,
          sumShelf: 0,
          minX: Infinity,
          maxX: -Infinity,
          runs: new Set(),
          shelves: new Set(),
        } as ValueStat);
      const width = helpers.width(item);
      // Runs laid end to end, far enough apart (raw planogram units) that none overlaps the next.
      const x = item.x + item.run * 10_000_000;
      stat.width += width;
      stat.sumX += (x + width / 2) * width;
      stat.sumShelf += item.shelf * width;
      stat.minX = Math.min(stat.minX, x);
      stat.maxX = Math.max(stat.maxX, x + width);
      stat.runs.add(item.run);
      stat.shelves.add(item.shelf);
      values.set(value, stat);
      byParent.set(parent, values);
    }
    byParent.forEach((values, parent) => {
      const stats = [...values.values()];
      for (let i = 0; i < stats.length; i++) {
        for (let j = i + 1; j < stats.length; j++) {
          const [a, b] = [stats[i], stats[j]].sort((left, right) =>
            left.value.localeCompare(right.value),
          );
          // Interleaved pairs flip on any small move — no reading order.
          const xOverlap = Math.max(0, Math.min(a.maxX, b.maxX) - Math.max(a.minX, b.minX));
          const xSeparated = xOverlap < 0.3 * Math.min(a.maxX - a.minX, b.maxX - b.minX);
          const sharedShelves = [...a.shelves].filter((shelf) => b.shelves.has(shelf)).length;
          const shelfSeparated =
            sharedShelves < 0.3 * Math.min(a.shelves.size, b.shelves.size) ||
            (sharedShelves === 0 && a.runs.size > 0);
          if (xSeparated) {
            relations.set(
              `${depth}|${parent}|${a.value}|${b.value}|x`,
              Math.sign(a.sumX / a.width - b.sumX / b.width),
            );
          } else if (shelfSeparated) {
            relations.set(
              `${depth}|${parent}|${a.value}|${b.value}|shelf`,
              Math.sign(a.sumShelf / a.width - b.sumShelf / b.width),
            );
          }
        }
      }
    });
  }
  return relations;
};

/** Appear/dissolve is fine; only a reversal is a swap. */
export const blockOrderViolations = (
  before: ReadonlyMap<string, number>,
  after: ReadonlyMap<string, number>,
): string[] => {
  const reversed: string[] = [];
  before.forEach((direction, key) => {
    const now = after.get(key);
    if (now !== undefined && direction !== 0 && now !== 0 && now !== direction) {
      reversed.push(key);
    }
  });
  return reversed;
};

const laneItemsOf = (studio: ResolverStudio, run: number, shelf: number): ContiguityPlanItem[] =>
  planOf(studio)
    .all()
    .filter((item) => item.run === run && item.shelf === shelf)
    .sort((a, b) => a.x - b.x);

/** Judged at the pin's own depth, not the vendored depth-2 node. */
export const intrudersCleared = (studio: ResolverStudio, pin: ResolverPin): boolean => {
  const plan = planOf(studio);
  const slack = slacksOf(plan.bayW).fill;
  const depth = pin.scope.split(" › ").length;
  const members = plan.all().filter((item) => plan.nodeKey(item, depth) === pin.scope);
  if (!members.length) return true;
  const lanes = new Set(members.map((item) => `${item.run}|${item.shelf}`));
  for (const laneKey of lanes) {
    const [run, shelf] = laneKey.split("|").map(Number);
    const lane = laneItemsOf(studio, run, shelf);
    const laneMembers = lane.filter((item) => plan.nodeKey(item, depth) === pin.scope);
    if (!laneMembers.length) continue;
    const firstX = laneMembers[0].x;
    const last = laneMembers[laneMembers.length - 1];
    const lastRight = last.x + last.w * last.hf;
    for (const item of lane) {
      if (!pin.ids.includes(item.id)) continue;
      if (item.x > firstX - slack && item.x + item.w * item.hf < lastRight + slack) return false;
    }
  }
  return true;
};

/** Vendored orphan refuses these; rotate on the same shelf. */
export const relocateIntruders = (
  studio: ResolverStudio,
  pin: ResolverPin,
): ResolverStep | null => {
  const plan = planOf(studio) as PlanForFragments & {
    cdtCompare: (a: unknown, b: unknown) => number;
    get: (id: string) => (ContiguityPlanItem & Record<string, unknown>) | undefined;
  };
  const depth = pin.scope.split(" › ").length;
  const slack = slacksOf(plan.bayW).fill;
  const restore = snapshotPlan(studio);
  const auditBefore = studio.audit();
  const orderBefore = blockOrderSignature(studio);
  const notes: string[] = [];
  let moved = 0;
  for (const intruderId of pin.ids) {
    const intruder = plan.get(intruderId);
    if (!intruder) continue;
    const lane = laneItemsOf(studio, intruder.run, intruder.shelf);
    const laneMembers = lane.filter((item) => plan.nodeKey(item, depth) === pin.scope);
    if (!laneMembers.length) continue;
    const firstX = laneMembers[0].x;
    const last = laneMembers[laneMembers.length - 1];
    const lastRight = last.x + last.w * last.hf;
    const width = intruder.w * intruder.hf;
    const inside = intruder.x > firstX - slack && intruder.x + width < lastRight + slack;
    if (!inside) continue;
    const goesAfter = plan.cdtCompare(intruder, last) > 0;
    if (goesAfter) {
      for (const item of lane) {
        if (item.id === intruderId) continue;
        if (item.x > intruder.x && item.x < lastRight) item.x = +(item.x - width).toFixed(2);
      }
      intruder.x = +(lastRight - width).toFixed(2);
    } else {
      for (const item of lane) {
        if (item.id === intruderId) continue;
        if (item.x >= firstX && item.x < intruder.x) item.x = +(item.x + width).toFixed(2);
      }
      intruder.x = +firstX.toFixed(2);
    }
    moved++;
    notes.push(
      `moved ${plan.nodeKey(intruder, depth)} to the block's ${goesAfter ? "right" : "left"} edge on shelf ${shelfNamesOf(studio, [intruder.shelf])}`,
    );
  }
  if (!moved) return null;
  const auditAfter = studio.audit();
  const regressions: string[] = [];
  if (auditAfter.overlaps > auditBefore.overlaps) regressions.push("overlaps");
  if (auditAfter.laneBreaks > auditBefore.laneBreaks) regressions.push("broken blocks");
  if (auditAfter.splitNodes > auditBefore.splitNodes) regressions.push("split blocks");
  if (auditAfter.tooTall > auditBefore.tooTall) regressions.push("packs too tall for the shelf");
  if (auditAfter.overruns > auditBefore.overruns) regressions.push("overhang");
  if (blockOrderViolations(orderBefore, blockOrderSignature(studio)).length)
    regressions.push("a flow change");
  if (regressions.length || !intrudersCleared(studio, pin)) {
    restore();
    return {
      pin: pin.scope,
      kind: pin.kind,
      action: "relocate",
      ok: false,
      resolved: false,
      physicalChange: false,
      reason: regressions.length
        ? `Not applied: moving it out would cause ${regressions.join(", ")}`
        : "Not applied: it could not be moved out of the block",
      notes,
      diff: { introduced: 0 },
    };
  }
  return {
    pin: pin.scope,
    kind: pin.kind,
    action: "relocate",
    ok: true,
    resolved: true,
    physicalChange: true,
    notes,
    diff: { introduced: 0 },
  };
};

export const snapshotPlan = (studio: ResolverStudio): (() => void) => {
  const plan = planOf(studio);
  const snapshot = plan.clone();
  return () => {
    plan.items.clear();
    snapshot.items.forEach((value, key) => plan.items.set(key, { ...value }));
  };
};

/** Grade is width-bound and strands items; relocate() is not. */
export const relocateOrderOutliers = (
  studio: ResolverStudio,
  pin: ResolverPin,
): ResolverStep | null => {
  const cdtResolve = (
    globalThis as unknown as {
      CDTResolve: CdtResolveOps & {
        audit: {
          sequenceBreaks: (
            plan: unknown,
            ids: readonly string[],
            orientation: string,
          ) => { order: readonly string[] };
        };
      };
    }
  ).CDTResolve;
  const plan = planOf(studio) as PlanForFragments & {
    get: (id: string) => { path: readonly (string | null)[] } | undefined;
    cdtCompare: (a: unknown, b: unknown) => number;
  };
  const { order } = cdtResolve.audit.sequenceBreaks(plan, [...pin.ids], "horizontal");
  const read = order.map((id) => ({ id, item: plan.get(id) })).filter((entry) => entry.item);
  if (read.length < 3) return null;
  const lengths = new Array<number>(read.length).fill(1);
  const previousIndex = new Array<number>(read.length).fill(-1);
  for (let i = 1; i < read.length; i++) {
    for (let j = 0; j < i; j++) {
      if (plan.cdtCompare(read[j].item, read[i].item) <= 0 && lengths[j] + 1 > lengths[i]) {
        lengths[i] = lengths[j] + 1;
        previousIndex[i] = j;
      }
    }
  }
  let best = 0;
  for (let i = 1; i < read.length; i++) if (lengths[i] > lengths[best]) best = i;
  const inChain = new Set<number>();
  for (let i = best; i >= 0; i = previousIndex[i]) inChain.add(i);
  const outliers = read.filter((_, index) => !inChain.has(index)).map((entry) => entry.id);
  if (outliers.length === 0 || outliers.length > 4) return null;

  const restore = snapshotPlan(studio);
  const before = studio.audit();
  const orderBefore = blockOrderSignature(studio);
  const notes: string[] = [];
  let moved = 0;
  for (const id of outliers) {
    let result: { ok: boolean; reason?: string; notes?: string[] };
    try {
      result = cdtResolveGlobal().ops.relocate(planOf(studio), [id], {});
    } catch {
      result = { ok: false, reason: "relocate failed" };
    }
    if (result.ok) {
      moved++;
      notes.push(`moved ${baseIdOf(id)} to its place in the block`);
    } else {
      notes.push(`${baseIdOf(id)} could not move: ${result.reason ?? "no room"}`);
    }
  }
  const after = studio.audit();
  const regressions: string[] = [];
  if (after.overlaps > before.overlaps) regressions.push("overlaps");
  if (after.overruns > before.overruns) regressions.push("overhang");
  if (after.tooTall > before.tooTall) regressions.push("packs too tall for the shelf");
  if (after.laneBreaks > before.laneBreaks) regressions.push("broken blocks");
  if (after.splitNodes > before.splitNodes) regressions.push("split blocks");
  if (after.invertedAvoidable > before.invertedAvoidable) regressions.push("sizes out of order");
  if (blockOrderViolations(orderBefore, blockOrderSignature(studio)).length) {
    regressions.push("a flow change");
  }
  if (moved === 0 || regressions.length) {
    restore();
    if (regressions.length) {
      return {
        pin: pin.scope,
        kind: pin.kind,
        action: "relocate",
        ok: true,
        resolved: false,
        physicalChange: false,
        reason: `Not applied: moving the stray facing(s) back would cause ${regressions.join(", ")}`,
        notes,
        diff: { introduced: 0 },
      };
    }
    return null;
  }
  return {
    pin: pin.scope,
    kind: pin.kind,
    action: "relocate",
    ok: true,
    resolved: false,
    partial: true,
    physicalChange: true,
    notes,
    diff: { introduced: 0 },
  };
};

const baseIdOf = (id: string): string => id.split("::")[0];
